<template>
  <div class="comment" :class="isAnswer ? 'answer-container' : null" :id="'comment-' + item.id">
    <div class="comment-info">
      <user-photo class="user-photo" :user="item.author" />
      <div class="user-details">
        <div class="user-name">
          {{ item.author.firstName + ' ' + item.author.lastName }}
        </div>
        <div class="user-job-title">
          {{ item.author.jobTitle }}
        </div>
      </div>
      <div class="spacer" />
      <div class="comment-date">
        {{ getDateFromNow(item.createdAt) }}
      </div>
    </div>
    <div v-if="editMode === false || item.author.id !== userId">
      <UserLikesModal v-if="item.likes && item.likes.length > 0" :likes="item.likes" :id-item="item.id"
        item-type="Comment" class="comment-likes" />
      <div class="comment-text" data-cy="comment-text"
        :class="(isAdminPage && isCommentReported) ? 'reported-text' : ''" v-linkify v-mentionify="goToUserInfo"
        v-html="content" />
      <div v-if="enableEdit && showDeleteText === false && showReportText === false">
        <div v-if="item.author.id === userId" class="comment-edit">
          <LikeCommentButton :comment="item" />
          <span class="option-separator">|</span>
          <span class="edit-a-comment" data-cy="edit-comment-button" @click="startEdit">{{ $t('post.comment.edit')
            }}</span>
          <span class="option-separator">|</span>
          <a class="answer-a-comment" data-cy="reply-comment-button" @click="startAnswer" v-scroll-to="vScrollTo">{{
            $t('post.comment.reply') }}</a>
          <span class="option-separator">|</span>
          <span v-if="isAnswer || item.children.length === 0" class="delete-a-comment" data-cy="delete-comment-button"
            @click="showDeleteConfirmation">{{ $t('post.comment.delete') }}</span>
        </div>
        <div v-else class="comment-edit">
          <LikeCommentButton :comment="item" />
          <span class="option-separator">|</span>
          <a class="answer-a-comment" @click="startAnswer" v-scroll-to="vScrollTo">{{ $t('post.comment.reply') }}</a>
          <span class="option-separator">|</span>
          <a class="report-a-comment" @click="showReportConfirmation(true)" v-scroll-to="vScrollTo">{{
            $t('post.comment.report') }}</a>
        </div>
      </div>
      <div v-if="showDeleteText === true" class="delete-text">
        <span>{{ $t('post.comment.deleteConfirmation') }}</span>
        <a class="delete-choice" data-cy="delete-comment-choice" @click="deleteComment">{{ $t('post.comment.yes') }}</a>
        <a class="delete-choice" @click="showDeleteText = false">{{ $t('post.comment.no') }}</a>
      </div>
      <div v-if="showReportText === true" class="report-text">
        <span>{{ $t('post.comment.reportConfirmation') }}</span>
        <a class="report-choice" @click="reportComment">{{ $t('post.comment.yes') }}</a>
        <a class="report-choice" @click="showReportConfirmation(false)">{{ $t('post.comment.no') }}</a>
      </div>
      <div v-if="showUnreportText === true" class="unreport-text">
        <span>{{ $t('post.comment.unreportConfirmation') }}</span>
        <a class="unreport-choice" @click="unreportComment">{{ $t('post.comment.yes') }}</a>
        <a class="unreport-choice" @click="showUnreportConfirmation(false)">{{ $t('post.comment.no') }}</a>
      </div>
      <div v-if="isAdminPage && isCommentReported">
        <div class="comment-edit" v-if="showDeleteText === false && showUnreportText === false">
          <span class="delete-a-comment" @click="showDeleteText = true">{{ $t('post.comment.delete') }}</span>
          <span class="option-separator">|</span>
          <span class="unreport-a-comment" @click="showUnreportConfirmation(true)">{{
            $t('post.comment.unreport')
          }}</span>
        </div>
      </div>
    </div>
    <comment-input v-else style="margin-left: 40px; width: calc(100% - 40px)" :button-name="$t('post.comment.modify')"
      :item="item" :comment="updatedEditedComment" :community-id="communityId" @send="editComment"
      @cancel="cancelEdit" />
    <div class="answer" v-if="isAnswer !== true">
      <div v-if="item.children.length > 2 && showHiddenAnswers === false" class="see-more">
        <div class="text-to-see-more" @click="showHiddenComments">
          <img src="@/assets/ArrowHLink14.svg" />
          <span> {{ $t('post.comment.displayPreviousComments', { count: hiddenAnswersNumber }) }} </span>
        </div>
        <div v-for="child in hiddenChildren" :key="child.id">
          <comment :enable-edit="enableEdit" :is-admin-page="isAdminPage" :item="child" :is-answer="true"
            @reply="emitReply" @edit="emitEdit" :community-id="communityId" :user-id="userId"
            :last-child="isLastChild(child)" />
        </div>
      </div>
      <div v-else class="see-more">
        <div v-if="showHiddenAnswers === true && hiddenAnswersNumber > 0" class="text-to-see-more"
          @click="showHiddenComments">
          <img src="@/assets/ArrowHLink14.svg" />
          <span> {{ $t('post.comment.hidePreviousComments', { count: hiddenAnswersNumber }) }} </span>
        </div>
        <div v-for="child in item.children" :key="child.id">
          <comment :enable-edit="enableEdit" :is-admin-page="isAdminPage" :item="child" :is-answer="true"
            :community-id="communityId" @reply="emitReply" @edit="emitEdit" :user-id="userId"
            :last-child="isLastChild(child)" />
        </div>
      </div>
    </div>
    <comment-input id="answerArea" v-if="answerMode === true"
      :style="!isAnswer ? 'margin-left: 40px; width: calc(100% - 40px)' : ''" :button-name="$t('post.comment.post')"
      :item="item" :comment="newComment" :community-id="communityId" @send="sendComment" @cancel="cancelAnswer" />
  </div>
</template>

<script>
import UserPhoto from '@/components/shared/UserPhoto'
import linkify from 'vue-linkify'
import ScrollTo from 'vue-scrollto'
import LikeCommentButton from '../like/LikeCommentButton'
import UserLikesModal from '../like/UserLikesModal'
import CommentInput from '@/components/post/CommentInput.vue'

export default {
  name: 'Comment',
  components: { CommentInput, UserPhoto, LikeCommentButton, UserLikesModal },
  directives: {
    linkify,
    ScrollTo
  },
  data() {
    return {
      showHiddenAnswers: false,
      container: document.getElementById('comments-container'),
      showDeleteText: false,
      showReportText: false,
      showUnreportText: false,
      editMode: false,
      answerMode: false,
      editedComment: '',
      newComment: ''
    }
  },
  props: {
    item: {
      type: Object,
      default: () => {
      }
    },
    userId: {
      type: String,
      required: true
    },
    communityId: {
      type: String,
      required: true
    },
    isAnswer: {
      type: Boolean,
      required: false
    },
    lastChild: {
      type: Boolean,
      required: false
    },
    enableEdit: {
      type: Boolean,
      required: false,
      default: true
    },
    isAdminPage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  methods: {
    addReplyMention(author) {
      this.newComment =
        `<p><span data-type="mention" \
class="mention" data-id="${author.id}" \
data-label="${author.firstName} ${author.lastName}" \
 contenteditable="false">@${author.firstName} ${author.lastName} \
</span></p>`
    },
    async editComment(comment, charCount) {
      if (charCount > 500 || charCount < 1) {
        return
      }
      this.editMode = false
      await this.$store.dispatch('editComment', {
        comment: { id: this.item.id, comment: comment }
      })
    },
    cancelEdit() {
      this.editedComment = this.item.comment
      this.editMode = false
    },
    cancelAnswer() {
      this.answerMode = false
    },
    async sendComment(comment, charCount) {
      if (charCount > 500 || charCount < 1) {
        return
      }
      this.answerMode = false
      if (this.item.parentId === null) {
        await this.$store.dispatch('addComment', { postId: this.item.idPost, comment: comment, parentId: this.item.id })
      } else {
        await this.$store.dispatch('addComment', {
          postId: this.item.idPost,
          comment: comment,
          parentId: this.item.parentId
        })
      }
    },
    goToUserInfo(userId) {
      this.$router.push({ name: 'userInfo', params: { id: userId } })
    },
    getLastChildId() {
      return this.item.children && this.item.children.length > 0 ? this.item.children[this.item.children.length - 1].id : this.item.id
    },
    getDateFromNow(date) {
      return this.$store.getters.getDateFromNow(date)
    },
    startEdit() {
      this.editedComment = this.item.comment
      this.editMode = true
      // this.emitEdit(this.item)
    },
    emitEdit(item) {
      this.$emit('edit', item)
    },
    startAnswer() {
      if (this.item.parentId) {
        this.addReplyMention(this.item.author)
      }
      this.answerMode = true
      // this.emitReply(this.item)
    },
    emitReply(item) {
      this.$emit('reply', item)
    },
    showDeleteConfirmation() {
      this.showDeleteText = true
    },
    async deleteComment() {
      if (this.isAdminPage && this.isCommentReported) {
        this.$store.dispatch('softDeleteReportedComment', { postId: this.item.idPost, commentId: this.item.id })
          .then(() => {
            this.showDeleteConfirmation(false)
          })
      } else {
        this.$store.dispatch('softDeleteComment', { postId: this.item.idPost, commentId: this.item.id })
          .then(deleted => {
            this.showDeleteText = !deleted
          })
      }
    },
    async reportComment() {
      this.$store.dispatch('reportComment', {
        postId: this.item.idPost,
        commentId: this.item.id,
        reporterId: this.userId
      })
        .then(reported => {
          this.showReportConfirmation(!reported)
          this.$toasted.global.success(this.$t('post.comment.sentToCAnnect'))
        })
    },
    async unreportComment() {
      this.$store.dispatch('unreportComment', { postId: this.item.idPost, commentId: this.item.id })
        .then(reported => {
          this.showUnreportConfirmation(false)
          if (reported) {
            this.$toasted.global.success(this.$t('post.comment.unreported'))
          }
        })
    },
    showHiddenComments() {
      this.showHiddenAnswers = !this.showHiddenAnswers
    },
    isLastChild(child) {
      return child === this.item.children[this.item.children.length - 1]
    },
    showReportConfirmation(val) {
      this.showReportText = val
    },
    showUnreportConfirmation(val) {
      this.showUnreportText = val
    }
  },
  computed: {
    content() {
      return this.$sanitizeHTML(this.$decodeHTML(this.item.comment))
    },
    updatedEditedComment() {
      return this.editedComment
    },
    hiddenAnswersNumber() {
      return this.item.children.length - 2
    },
    hiddenChildren() {
      return this.item.children.slice(Math.max(this.item.children.length - 2, 1))
    },
    vScrollTo() {
      return { el: '#comment-' + this.getLastChildId(), container: '#comments-container' }
    },
    isCommentReported() {
      return this.item.reportCount > 0
    }
  }
}
</script>

<style scoped lang="scss">
.edit-comment-input {
  margin-top: 10px;
}

.comment {
  margin: 8px 24px;

  &.answer-container {
    margin: 8px 0 8px 40px;
  }

  .comment-info {
    display: flex;
    align-items: center;

    .user-photo {
      height: 32px;
    }

    .user-details {
      margin-left: 8px;

      .user-name {
        font-weight: bold;
        font-size: 14px;
        color: $black-color;
        letter-spacing: 0;
        line-height: 21px;
      }

      .user-job-title {
        font-size: 13px;
        color: #999999;
        letter-spacing: 0;
        line-height: 19px;
      }
    }

    .spacer {
      flex-grow: 1;
    }

    .comment-date {
      font-size: 13px;
      color: #999999;
      letter-spacing: 0;
      text-align: right;
      line-height: 19px;
    }
  }

  .delete-text,
  .report-text,
  .unreport-text {
    padding-top: 4px;
    margin-left: 40px;
    font-size: 13px;
    color: $black-color;

    .delete-choice,
    .report-choice,
    .unreport-choice {
      cursor: pointer;
      padding-left: 24px;
      color: $primary-color;
    }

  }

  .comment-edit {
    padding-top: 4px;
    display: flex;
    justify-content: flex-start;
    margin-left: 40px;
    font-size: 13px;

    .option-separator {
      padding: 0 10px;
    }

    .edit-a-comment,
    .delete-a-comment,
    .answer-a-comment,
    .report-a-comment,
    .unreport-a-comment {
      color: $primary-color;
      cursor: pointer;
    }

    .edited-comment {
      font-size: 13px;
      color: #999999;
      padding-left: 30px;
    }
  }

  .answer {
    width: 100%;

    .see-more {
      font-size: 14px;
      color: $primary-color;

      .text-to-see-more {
        cursor: pointer;
        display: flex;
        margin: 8px 24px 8px 40px;
      }
    }
  }
}

.comment-likes {
  float: right;
  font-size: 12px;
  color: #7A6855;
}
</style>
<style lang="scss">
.comment-text {
  margin-left: 40px;
  font-size: 13px;
  color: $black-color;
  letter-spacing: 0;
  line-height: 25px;
  overflow-wrap: break-word;

  &>*+* {
    margin-top: 0.75em;
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
    min-height: 10px;
  }

  &.reported-text {
    color: $cerise-color;

    ::v-deep a {
      color: $cerise-color;
    }
  }
}
</style>
